import React, { useEffect, useState } from 'react';

type PreviewBrief = {
  reference: string;
  businessName: string;
  industry: string;
  town: string;
  services: string[];
  budget: string;
  urgency: string;
  contactName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  recommendation?: {
    mainService?: string;
    overall?: number;
    priceBand?: string;
    timeline?: string;
    nextAction?: string;
    upsell?: string;
  };
  publicMessage?: string;
};

const STATUS_STEPS = [
  'Brief Received',
  'Under Review',
  'Preview / Callback',
  'Proposal Ready',
  'Approved / Build Queue'
];

const statusIndex = (status: string) => {
  const value = (status || '').toLowerCase();
  if (value.includes('approved')) return 4;
  if (value.includes('preview') || value.includes('callback')) return 2;
  if (value.includes('under review')) return 1;
  if (value.includes('proposal')) return 3;
  return 0;
};

export default function NexoraXClientPreviewPortal() {
  const [reference, setReference] = useState('');
  const [email, setEmail] = useState('');
  const [brief, setBrief] = useState<PreviewBrief | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [clientNote, setClientNote] = useState('');

  // CLIENT_PREVIEW_QUERY_PARAM_FIX
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get('reference') || params.get('ref') || '';
    const emailFromUrl = params.get('email') || '';

    if (refFromUrl) setReference(refFromUrl);
    if (emailFromUrl) setEmail(emailFromUrl);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get('reference') || params.get('ref') || '';
    const emailFromUrl = params.get('email') || '';

    if (refFromUrl && emailFromUrl && !brief && !loading) {
      setTimeout(() => {
        const event = new Event('submit', { bubbles: true, cancelable: true });
        const form = document.getElementById('client-preview-lookup-form');
        form?.dispatchEvent(event);
      }, 300);
    }
  }, [brief, loading]);

  const lookupBrief = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setNotice('');
    setBrief(null);

    if (!reference.trim() || !email.trim()) {
      setNotice('Please enter your reference number and email address.');
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        reference: reference.trim(),
        email: email.trim()
      });

      const response = await fetch(`/api/client-preview?${params.toString()}`);
      const data = await response.json();

      if (!data.success) {
        setNotice(data.error || 'We could not find that preview reference.');
        return;
      }

      setBrief(data.brief);
      setNotice('Preview status found. This is the correct client tracking page.');
    } catch {
      setNotice('Preview lookup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyTrackingLink = async () => {
    if (!brief) return;

    const link = `${window.location.origin}/client-preview?reference=${encodeURIComponent(brief.reference)}&email=${encodeURIComponent(email)}`;

    try {
      await navigator.clipboard.writeText(link);
      setNotice('Tracking link copied. You can now paste it into WhatsApp, email, TikTok message or client chat.');
    } catch {
      setNotice('Could not copy link. Please copy the browser address manually.');
    }
  };

  const sendClientRequest = async (requestType: string) => {
    if (!brief) return;

    setLoading(true);
    setNotice('');

    try {
      const response = await fetch('/api/client-preview/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reference: brief.reference,
          email,
          requestType,
          clientNote
        })
      });

      const data = await response.json();

      if (!data.success) {
        setNotice(data.error || 'Request could not be sent.');
        return;
      }

      setBrief(data.brief);
      setNotice(`${requestType} sent to NexoraX. The status has been updated. Mahmood can now see this inside Remote Workplace Smart Quotes.`);
    } catch {
      setNotice('Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const active = statusIndex(brief?.status || '');

  // CLIENT_PREVIEW_TRACKING_URL_CONST_V2
  const trackingUrl = brief
    ? `${window.location.origin}/client-preview?reference=${encodeURIComponent(brief.reference)}&email=${encodeURIComponent(email)}`
    : '';

  return (
    <main className="min-h-screen overflow-hidden bg-[#02030a] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(0,240,255,0.18),transparent_35%),radial-gradient(circle_at_85%_20%,rgba(168,85,247,0.20),transparent_35%),linear-gradient(180deg,#02030a,#050014_55%,#02030a)]" />
      <div className="pointer-events-none fixed inset-0 opacity-30 [background-image:linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.05)_1px,transparent_1px)] [background-size:42px_42px]" />

      <section className="relative mx-auto max-w-7xl px-5 py-10 md:py-16">
        <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_60px_rgba(0,240,255,0.12)] backdrop-blur-xl md:p-10">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.45em] text-cyan-300">
                NexoraX Client Preview Portal
              </p>
              <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight md:text-6xl">
                Track your digital brief, preview and next step.
              </h1>
              <p className="mt-5 max-w-3xl text-slate-300">
                Enter your Smart Quote reference and email to check your NexoraX review status, recommended package and next action.
              </p>
            </div>

            <div className="rounded-3xl border border-purple-500/30 bg-purple-950/20 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">Client Access</p>
              <p className="mt-2 text-2xl font-black text-cyan-300">Secure Lookup</p>
              <p className="mt-1 text-xs text-slate-400">Reference + email required</p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.75fr_1.25fr]">
          <section className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(0,240,255,0.10)] backdrop-blur-xl">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
              Find your brief
            </p>
            <h2 className="mt-2 text-2xl font-black">Client reference lookup</h2>

            <form id="client-preview-lookup-form" onSubmit={lookupBrief} className="mt-6 space-y-4">
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Enter reference e.g. NXQ-518626"
                className="w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email used on Smart Quote"
                className="w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50 disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'Check Preview Status'}
              </button>
            </form>

            {notice && (
              <div className="mt-5 rounded-2xl border border-purple-500/30 bg-purple-950/20 p-4 text-sm text-purple-100">
                {notice}
              </div>
            )}

            <div className="mt-6 rounded-3xl border border-pink-500/20 bg-pink-950/10 p-5">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-pink-300">
                Need a reference?
              </p>
              <p className="mt-3 text-sm text-slate-300">
                Submit a Smart Quote first, then use the reference shown after submission.
              </p>
              <a
                href="/smart-quote"
                className="mt-4 inline-flex rounded-xl border border-pink-500/40 bg-pink-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-pink-100 hover:bg-pink-900/40"
              >
                Submit New Smart Quote
              </a>
            </div>
          </section>

          <section className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(168,85,247,0.10)] backdrop-blur-xl">
            {!brief ? (
              <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-zinc-800 bg-black/50 p-8 text-center">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                    Awaiting reference
                  </p>
                  <h3 className="mt-3 text-3xl font-black">Your client preview will appear here.</h3>
                  <p className="mt-3 max-w-xl text-slate-400">
                    Customers can use this portal after submitting the Smart Quote form. It gives them a professional status page instead of waiting with no update.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                      Reference {brief.reference}
                    </p>
                    <h2 className="mt-2 text-3xl font-black text-white">
                      {brief.businessName}
                    </h2>
                    <p className="mt-2 text-sm text-slate-400">
                      {brief.industry || 'Business'} • {brief.town || 'Location not provided'}
                    </p>
                  </div>

                  <span className="rounded-full border border-purple-500/40 bg-purple-950/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100">
                    {brief.status}
                  </span>
                </div>

                <div className="mt-6 rounded-3xl border border-cyan-500/20 bg-cyan-950/10 p-5">
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                    Client tracking link
                  </p>
                  <p className="mt-3 text-sm text-slate-300">
                    This is the client status page for this Smart Quote. The customer can return here using their reference and email.
                  </p>
                  <div className="mt-4 rounded-2xl border border-zinc-800 bg-black/70 p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      CLIENT_PREVIEW_VISIBLE_TRACKING_URL_V2
                    </p>
                    <input
                      readOnly
                      value={trackingUrl}
                      className="mt-2 w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-xs text-cyan-100 outline-none"
                      onFocus={(e) => e.target.select()}
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={copyTrackingLink}
                      className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
                    >
                      Copy Tracking Link
                    </button>

                    <a
                      href={trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border border-cyan-500/40 bg-black px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-950/40"
                    >
                      Open Tracking Link
                    </a>

                    <a
                      href="/smart-quote"
                      className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                    >
                      Submit New Smart Quote
                    </a>
                  </div>
                  <p className="mt-3 hidden">CLIENT_PREVIEW_TRACKING_LINK_PANEL</p>
                </div>

                <div className="mt-6 rounded-3xl border border-cyan-500/20 bg-cyan-950/10 p-5">
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                    Project timeline
                  </p>
                  <div className="mt-5 grid gap-3 md:grid-cols-5">
                    {STATUS_STEPS.map((step, index) => (
                      <div
                        key={step}
                        className={`rounded-2xl border p-4 text-sm ${
                          index <= active
                            ? 'border-cyan-400/40 bg-cyan-950/30 text-cyan-100'
                            : 'border-zinc-800 bg-black/60 text-slate-500'
                        }`}
                      >
                        <p className="font-mono text-[10px] uppercase tracking-widest">
                          Step {index + 1}
                        </p>
                        <p className="mt-2 font-bold">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-3">
                  <div className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Recommended</p>
                    <p className="mt-2 text-xl font-black text-cyan-300">
                      {brief.recommendation?.mainService || 'Review in progress'}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Growth Score</p>
                    <p className="mt-2 text-3xl font-black text-purple-300">
                      {brief.recommendation?.overall || '-'}%
                    </p>
                  </div>

                  <div className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Timeline</p>
                    <p className="mt-2 text-sm font-bold text-white">
                      {brief.recommendation?.timeline || 'To be confirmed'}
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-3xl border border-purple-500/20 bg-purple-950/10 p-5">
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                    NexoraX update
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-200">
                    {brief.publicMessage}
                  </p>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Services requested</p>
                      <p className="mt-2 text-sm text-white">
                        {(brief.services || []).join(', ') || 'No services selected'}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Budget / urgency</p>
                      <p className="mt-2 text-sm text-white">{brief.budget || 'Not provided'}</p>
                      <p className="text-xs text-cyan-300">{brief.urgency || 'No urgency provided'}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-3xl border border-pink-500/20 bg-pink-950/10 p-5">
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-pink-300">
                    Request next action
                  </p>
                  <textarea
                    value={clientNote}
                    onChange={(e) => setClientNote(e.target.value)}
                    placeholder="Optional message for NexoraX..."
                    className="mt-4 min-h-[90px] w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-pink-400"
                  />

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => sendClientRequest('Preview Requested')}
                      className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40 disabled:opacity-50"
                    >
                      Request Preview
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => sendClientRequest('Callback Requested')}
                      className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-900/40 disabled:opacity-50"
                    >
                      Request Callback
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}
